import React, { useState, useEffect, useMemo } from "react";
import { styled } from "@mui/material/styles";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import {
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    CircularProgress,
    Box,
    IconButton,
    Checkbox,
    Typography,
    Button,
    Tooltip,
    Dialog
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import PublishIcon from '@mui/icons-material/Publish';
import UploadCsvModal from "./UploadCsvModal";
import { verifyMentorReviews, publishMentorReviews, bulkEmailMentorReviews } from "../../../services/mentorServices/ReviewServices";
import { toast } from "react-toastify";
import Papa from "papaparse";
import "./MentorReview.css";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#7e7e7e",
        color: theme.palette.common.white,
        textTransform: "capitalize",
        fontSize: 14,
        fontWeight: 600,
        letterSpacing: "0.5px",
        borderBottom: "1px solid #e0e0e0",
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        color: "#333",
        borderBottom: "1px solid #e0e0e0",
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd),&:nth-of-type(even)": {
        backgroundColor: theme.palette.common.white,
    },
    "&:last-child td, &:last-child th": {
        border: 0,
    },
    "&.Mui-selected, &.Mui-selected:hover": {
        backgroundColor: "rgba(25, 118, 210, 0.08)",
    }
}));

const MentorReviewTable = ({ data, loading, totalCount, currentPage = 1, selectedReviewName, onPageChange, onRowsPerPageChange, onRefresh, onDownloadCSV, downloadingCSV = false, isAdmin = false, sortFilterNode, onPublish }) => {
    const [page, setPage] = useState(currentPage ? currentPage - 1 : 0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedRows, setSelectedRows] = useState([]);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
    const navigate = useNavigate();
    const { coeName } = useParams();

    const user = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user')) || {};
        } catch {
            return {};
        }
    }, []);

    const userRole = user?.role || '';
    const isSuperAdmin = userRole === 'super_admin';
    const isCoeAdmin = userRole === 'coe_admin' || userRole === 'coe_report_admin';
    const isMentor = userRole === 'mentor' || (!isSuperAdmin && !isCoeAdmin && userRole !== 'coe_report_viewer');
    const showCheckboxes = isMentor || isCoeAdmin;

    const getBaseReviewPath = () => {
        if (coeName) return `/${coeName}/learner-reviews`;
        if (userRole === 'coe_report_viewer') {
            const formattedCoeName = user?.coeName ? user.coeName.trim().replace(/\s+/g, '-') : (user?.coe_name ? user.coe_name.trim().replace(/\s+/g, '-') : 'coe');
            return `/${formattedCoeName}/learner-reviews`;
        }
        return isAdmin ? '/admin/learner-reviews' : '/mentor/learner-reviews';
    };

    useEffect(() => {
        if (currentPage !== undefined && currentPage !== null) {
            setPage(currentPage - 1);
        }
    }, [currentPage]);

    useEffect(() => {
        setPage(0);
    }, [selectedReviewName]);

    useEffect(() => {
        setSelectedRows([]);
    }, [currentPage, selectedReviewName, rowsPerPage]);

    useEffect(() => {
        if (totalCount !== undefined && totalCount > 0 && page * rowsPerPage >= totalCount) {
            setPage(0);
        }
    }, [totalCount, page, rowsPerPage]);

    const isRowSelectable = (row) => {
        const s = (row?.status || '').toLowerCase().trim();
        if (isCoeAdmin) {
            return s === 'completed';
        }
        if (isMentor) {
            return s === 'verification_pending';
        }
        return false;
    };

    const selectableRows = (data || []).filter(row => isRowSelectable(row));

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        if (onPageChange) {
            onPageChange(newPage + 1, rowsPerPage);
        }
    };

    const handleChangeRowsPerPage = (event) => {
        const newLimit = parseInt(event.target.value, 10);
        setRowsPerPage(newLimit);
        setPage(0);
        if (onRowsPerPageChange) {
            onRowsPerPageChange(1, newLimit);
        }
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const newSelecteds = selectableRows.map((n) => {
                const index = (data || []).indexOf(n);
                return n.reviewProgressId || n.submissionId || n.submission_id || n.id || `sub_${index}`;
            });
            setSelectedRows(newSelecteds);
            return;
        }
        setSelectedRows([]);
    };

    const handleSelectRow = (event, id) => {
        const selectedIndex = selectedRows.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selectedRows, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selectedRows.slice(1));
        } else if (selectedIndex === selectedRows.length - 1) {
            newSelected = newSelected.concat(selectedRows.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selectedRows.slice(0, selectedIndex),
                selectedRows.slice(selectedIndex + 1),
            );
        }
        setSelectedRows(newSelected);
    };

    // Server-paginated data (totalCount > data length) is already one page; otherwise slice client-side.
    const isServerPaginated = !!onPageChange && totalCount !== undefined && totalCount > (data || []).length;
    const paginatedRows = isServerPaginated
        ? (data || []).slice(0, rowsPerPage)
        : (data || []).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const getStatusChipClass = (status) => {
        switch ((status || '').toLowerCase()) {
            case 'review_pending':
            case 'lc_pending':
            case 'verification_pending':
            case 'pending': return 'pending';
            case 'evaluation_pending': return 'evaluation-pending';
            case 'evaluation_failed': return 'evaluation-failed';
            case 'tlc_published': return 'tlc-published';
            case 'completed': return 'completed';
            case 'reviewed': return 'reviewed';
            case 'in progress': return 'in-progress';
            case 'email_queued':
            case 'email_sending': return 'in-progress';
            case 'email_failed': return 'evaluation-failed';
            default: return 'pending';
        }
    };

    const formatAiScore = (score) => {
        if (score === null || score === undefined) return <span style={{ fontWeight: 700 }}>--</span>;
        let color = '#212529';
        if (score >= 80) color = '#2e7d32';
        else if (score >= 50) color = '#1976d2';

        return <span style={{ color: color, fontWeight: 700 }}>{score}</span>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return { date: "-", time: "" };
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return { date: dateString, time: "" };

        const optionsDate = { month: 'short', day: 'numeric', year: 'numeric' };
        const dateFormatted = date.toLocaleDateString('en-US', optionsDate);

        const optionsTime = { hour: 'numeric', minute: '2-digit', hour12: true };
        const timeFormatted = date.toLocaleTimeString('en-US', optionsTime);

        return {
            date: dateFormatted,
            time: timeFormatted
        };
    };

    const handleDownloadCSV = () => {
        if (onDownloadCSV) {
            onDownloadCSV();
            return;
        }

        if (!data || data.length === 0) {
            toast.info("No Learner Review data available to download.");
            return;
        }

        const formattedData = data.map((item, index) => {
            const learnerName = item.learnerName || item.learner_name || item?.Learner?.name || item?.learner?.name || item?.name || "-";
            const rollNo = item.rollNo || item.rollNumber || item.roll_number || item?.Learner?.rollNumber || item?.Learner?.roll_number || item?.Learner?.rollNo || item?.Learner?.rollNum || item?.learner?.roll_num || item?.roll_num || item?.rollNum || "-";
            const emailId = item.emailId || item.email_id || item?.Learner?.emailId || item?.Learner?.email || item?.Learner?.email_id || item?.learner?.email || item?.email || "-";
            const labName = item.labName || item.lab_name || item?.Learner?.labName || item?.Learner?.lab_name || item?.learner?.lab_name || item?.learner?.labName || "-";
            const reviewName = item.reviewName || item.reviewTitle || item.review_name || "-";
            const subDateRaw = item.submissionDate || item.submission_date || item.created_at || item.submittedDate || item.submitted_date;
            const subDate = subDateRaw ? new Date(subDateRaw).toLocaleString('en-US') : "-";

            const tScore = item.techExcellence !== undefined && item.techExcellence !== null ? item.techExcellence : "-";
            const lScore = item.learnability !== undefined && item.learnability !== null ? item.learnability : "-";
            const cScore = item.communicability !== undefined && item.communicability !== null ? item.communicability : "-";

            let totalScoreStr = "-";
            if (tScore !== "-" || lScore !== "-" || cScore !== "-") {
                const t = tScore !== "-" ? Number(tScore) : 0;
                const l = lScore !== "-" ? Number(lScore) : 0;
                const c = cScore !== "-" ? Number(cScore) : 0;
                totalScoreStr = (t + l + c).toFixed(2);
            } else if (item.totalScore !== undefined && item.totalScore !== null) {
                totalScoreStr = Number(item.totalScore).toFixed(2);
            } else if (item.total_score !== undefined && item.total_score !== null) {
                totalScoreStr = Number(item.total_score).toFixed(2);
            }

            const status = (item.status || "PENDING").replace(/_/g, ' ');

            return {
                "S.No": index + 1,
                "Roll Number": rollNo,
                "Email": emailId,
                "Lab Name": labName,
                "Learner Name": learnerName,
                "Review Name": reviewName,
                "Submission Date": subDate,
                "Techability (T)": tScore,
                "Learnability (L)": lScore,
                "Communicability (C)": cScore,
                "Total Score": totalScoreStr,
                "Status": status
            };
        });

        const csvContent = Papa.unparse(formattedData);
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const cleanName = (selectedReviewName && selectedReviewName !== 'all')
            ? selectedReviewName.replace(/[^a-zA-Z0-9]/g, '_')
            : 'All_Reviews';
        link.setAttribute("download", `Learner_Review_Scores_${cleanName}_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success("CSV file downloaded successfully!");
    };

    const handleMarkAsVerified = async () => {
        if (selectedRows.length === 0) return;
        setVerifying(true);
        try {
            const reviewsToVerify = selectedRows.map(id => {
                const row = (data || []).find((r, index) => {
                    const rId = r.reviewProgressId || r.submissionId || r.submission_id || r.id || `sub_${index}`;
                    return rId === id;
                });
                return {
                    learnerId: row?.learnerId,
                    reviewProgressId: id
                };
            }).filter(item => item.learnerId && item.reviewProgressId);

            const payload = {
                reviews: reviewsToVerify
            };

            const res = await verifyMentorReviews(payload);
            if (res?.data?.success) {
                toast.success("Selected reviews marked as verified successfully.");
                setSelectedRows([]);
                setIsVerifyModalOpen(false);
                if (onRefresh) onRefresh();
            } else {
                toast.error(res?.data?.message || "Failed to verify reviews.");
            }
        } catch (error) {
            console.error("Verify error:", error);
            toast.error(error?.response?.data?.message || "Error verifying reviews.");
        } finally {
            setVerifying(false);
        }
    };

    const handlePublishReviews = async () => {
        if (selectedRows.length === 0) return;
        setPublishing(true);
        try {
            const reviewsToPublish = selectedRows.map(id => {
                const row = (data || []).find((r, index) => {
                    const rId = r.reviewProgressId || r.submissionId || r.submission_id || r.id || `sub_${index}`;
                    return rId === id;
                });
                return {
                    learnerId: row?.learnerId,
                    reviewProgressId: id
                };
            }).filter(item => item.learnerId && item.reviewProgressId);

            const payload = {
                reviews: reviewsToPublish
            };

            const res = onPublish ? await onPublish(payload, selectedRows) : await bulkEmailMentorReviews(payload);
            if (res?.data?.success || res?.status === 200) {
                toast.success(res?.data?.message || "Selected review(s) published successfully.");
                setSelectedRows([]);
                setIsPublishModalOpen(false);
                if (onRefresh) onRefresh();
            } else {
                toast.error(res?.data?.message || "Failed to publish review(s).");
            }
        } catch (error) {
            console.error("Publish review error:", error);
            toast.error(error?.response?.data?.message || error?.message || "Error publishing review(s).");
        } finally {
            setPublishing(false);
        }
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 1, flexWrap: 'wrap', gap: 2 }}>
                <Typography sx={{ color: '#1976d2', fontWeight: 700, fontSize: '14px', width: { xs: '100%', sm: 'auto' } }}>
                    {showCheckboxes ? (
                        `${selectedRows.length} / ${totalCount !== undefined ? totalCount : (data?.length || 0)} Reviews Selected`
                    ) : (
                        `Total Reviews: ${totalCount !== undefined ? totalCount : (data?.length || 0)}`
                    )}
                </Typography>
                <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
                    {data && data.length > 0 && (
                        <>
                            {isCoeAdmin && selectedRows.length > 0 && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => setIsPublishModalOpen(true)}
                                    disabled={publishing}
                                    sx={{
                                        borderRadius: '4px',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        padding: '6px 16px',
                                        boxShadow: 'none',
                                        width: { xs: '100%', sm: 'auto' }
                                    }}
                                    startIcon={publishing ? <CircularProgress size={18} color="inherit" /> : <PublishIcon sx={{ fontSize: '18px' }} />}
                                >
                                    {publishing ? "Publishing..." : "Publish Reviews"}
                                </Button>
                            )}
                            {!isAdmin && selectedRows.length > 0 && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => setIsVerifyModalOpen(true)}
                                    disabled={verifying}
                                    sx={{ borderRadius: '4px', textTransform: 'none', fontWeight: 600, padding: '6px 16px', boxShadow: 'none', width: { xs: '100%', sm: 'auto' } }}
                                    startIcon={verifying ? <CircularProgress size={18} color="inherit" /> : <CheckCircleOutlineIcon sx={{ fontSize: '18px' }} />}
                                >
                                    {verifying ? "Verifying..." : "Mark as Verified"}
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                color="success"
                                onClick={handleDownloadCSV}
                                disabled={downloadingCSV}
                                sx={{ borderRadius: '4px', textTransform: 'none', fontWeight: 600, padding: '6px 16px', width: { xs: '100%', sm: 'auto' } }}
                                startIcon={downloadingCSV ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon sx={{ fontSize: '18px' }} />}
                            >
                                {downloadingCSV ? "Downloading..." : "Download Scores CSV"}
                            </Button>
                            {!isAdmin && (
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => setIsUploadModalOpen(true)}
                                    sx={{ borderRadius: '4px', textTransform: 'none', fontWeight: 600, padding: '6px 16px', backgroundColor: '#fff', width: { xs: '100%', sm: 'auto' } }}
                                    startIcon={<CloudUploadIcon sx={{ fontSize: '18px' }} />}
                                >
                                    Upload L&C CSV
                                </Button>
                            )}
                        </>
                    )}
                    {sortFilterNode && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {sortFilterNode}
                        </Box>
                    )}
                </Box>
            </Box>

            <TableContainer component={Paper} className="table-container" sx={{ width: "100%", overflowX: "auto", boxShadow: 'none', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                <Table sx={{ tableLayout: "auto", width: "max-content", minWidth: "100%" }} aria-label="customized table">
                    <TableHead>
                        <TableRow>
                            {showCheckboxes && (
                                <StyledTableCell padding="checkbox">
                                    <Tooltip 
                                        title={
                                            selectableRows.length === 0 
                                                ? (isCoeAdmin ? "No 'Completed' items to select" : "No 'Verification Pending' items to select") 
                                                : ""
                                        } 
                                        arrow 
                                        placement="top"
                                    >
                                        <span>
                                            <Checkbox
                                                indeterminate={selectedRows.length > 0 && selectedRows.length < selectableRows.length}
                                                checked={selectableRows.length > 0 && selectedRows.length === selectableRows.length}
                                                onChange={handleSelectAll}
                                                disabled={selectableRows.length === 0}
                                                size="small"
                                                sx={{
                                                    color: 'white',
                                                    '&.Mui-checked': {
                                                        color: 'white',
                                                    },
                                                    '&.MuiCheckbox-indeterminate': {
                                                        color: 'white',
                                                    },
                                                    '&.Mui-disabled': {
                                                        color: 'rgba(255, 255, 255, 0.3)',
                                                    }
                                                }}
                                            />
                                        </span>
                                    </Tooltip>
                                </StyledTableCell>
                            )}
                            <StyledTableCell>ROLL NUMBER</StyledTableCell>
                            <StyledTableCell>LEARNER NAME</StyledTableCell>
                            <StyledTableCell>EMAIL</StyledTableCell>
                            <StyledTableCell>LAB NAME</StyledTableCell>
                            <StyledTableCell>REVIEW NAME</StyledTableCell>
                            <StyledTableCell>SUBMISSION DATE</StyledTableCell>
                            <StyledTableCell>T | L | C SCORE</StyledTableCell>
                            <StyledTableCell>TOTAL SCORE</StyledTableCell>
                            <StyledTableCell>STATUS</StyledTableCell>
                            <StyledTableCell align="right">ACTIONS</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <StyledTableCell colSpan={showCheckboxes ? 11 : 10} align="center">
                                    <Box display="flex" justifyContent="center" alignItems="center" height="100px">
                                        <CircularProgress />
                                    </Box>
                                </StyledTableCell>
                            </TableRow>
                        ) : paginatedRows?.length > 0 ? (
                            paginatedRows.map((row, index) => {
                                const rollNumber = row.rollNo || row.roll_number || row?.Learner?.rollNo || row?.Learner?.roll_number || row?.Learner?.rollNum || row?.learner?.roll_num || row?.roll_num || row?.rollNum || "-";
                                const learnerName = row.learnerName || row.learner_name || row?.Learner?.name || row?.learner?.name || row?.name || "-";
                                const emailId = row.emailId || row.email_id || row?.Learner?.emailId || row?.Learner?.email || row?.Learner?.email_id || row?.learner?.email || row?.email || "-";
                                const labName = row.labName || row.lab_name || row?.Learner?.labName || row?.Learner?.lab_name || row?.learner?.lab_name || row?.learner?.labName || "-";
                                const subDateRaw = row.submissionDate || row.submission_date || row.created_at || row.submittedDate || row.submitted_date;
                                const { date: subDate, time: subTime } = formatDate(subDateRaw);
                                const status = row.status || "Pending";
                                const reviewName = row.reviewName || row.reviewTitle || "-";
                                const globalIndex = page * rowsPerPage + index;
                                const subId = row.reviewProgressId || row.submissionId || row.submission_id || row.id || `sub_${globalIndex}`;
                                const learnerId = row.learnerId;

                                const tScore = row.techExcellence !== undefined && row.techExcellence !== null ? row.techExcellence : "-";
                                const lScore = row.learnability !== undefined && row.learnability !== null ? row.learnability : "-";
                                const cScore = row.communicability !== undefined && row.communicability !== null ? row.communicability : "-";
                                const hasTlcScore = tScore !== "-" || lScore !== "-" || cScore !== "-";

                                let totalScoreStr = "-";
                                if (hasTlcScore) {
                                    const t = tScore !== "-" ? Number(tScore) : 0;
                                    const l = lScore !== "-" ? Number(lScore) : 0;
                                    const c = cScore !== "-" ? Number(cScore) : 0;
                                    const sum = t + l + c;
                                    totalScoreStr = sum === 0 ? "0" : sum.toFixed(2);
                                }

                                const canSelect = isRowSelectable(row);
                                const disabledTooltip = !canSelect 
                                    ? (isCoeAdmin ? "Only 'Completed' items can be selected" : "Only 'Verification Pending' items can be selected")
                                    : "";
                                const isSelected = selectedRows.indexOf(subId) !== -1;

                                return (
                                    <StyledTableRow key={index} selected={isSelected} hover>
                                        {showCheckboxes && (
                                            <StyledTableCell padding="checkbox">
                                                <Tooltip title={disabledTooltip} arrow placement="top">
                                                    <span>
                                                        <Checkbox
                                                            checked={isSelected}
                                                            disabled={!canSelect}
                                                            onChange={(event) => handleSelectRow(event, subId)}
                                                            size="small"
                                                            color="primary"
                                                        />
                                                    </span>
                                                </Tooltip>
                                            </StyledTableCell>
                                        )}
                                        <StyledTableCell>{rollNumber}</StyledTableCell>
                                        <StyledTableCell sx={{ fontWeight: 500 }}>{learnerName}</StyledTableCell>
                                        <StyledTableCell>{emailId}</StyledTableCell>
                                        <StyledTableCell>{labName}</StyledTableCell>
                                        <StyledTableCell>{reviewName}</StyledTableCell>
                                        <StyledTableCell>
                                            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: "center", gap: 1 }}>
                                                <span>{subDate}</span>
                                                {subTime && <span style={{ fontSize: '12px', color: '#6c757d' }}>{subTime}</span>}
                                            </Box>
                                        </StyledTableCell>
                                        <StyledTableCell >{hasTlcScore ? `${tScore} | ${lScore} | ${cScore}` : "-"}</StyledTableCell>
                                        <StyledTableCell >{totalScoreStr}</StyledTableCell>
                                        <StyledTableCell>
                                            <span className={`status-chip ${getStatusChipClass(status)}`}>
                                                {status.replace(/_/g, ' ')}
                                            </span>
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            <Typography 
                                                component="span" 
                                                onClick={() => navigate(`${getBaseReviewPath()}/${subId}`, { 
                                                    state: { 
                                                        learnerId,
                                                        learnerName: learnerName !== '-' ? learnerName : undefined,
                                                        labName: labName !== '-' ? labName : undefined,
                                                        reviewName: (reviewName && reviewName !== '-') ? reviewName : selectedReviewName,
                                                        fromReportViewer: Boolean(coeName || userRole === 'coe_report_viewer')
                                                    } 
                                                })}
                                                sx={{ color: '#1976d2', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', '&:hover': { color: '#115293' }, fontSize: '13.5px' }}
                                            >
                                                View
                                            </Typography>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                );
                            })
                        ) : (
                            <StyledTableRow>
                                <StyledTableCell colSpan={showCheckboxes ? 11 : 10} align="center" sx={{ fontWeight: "600", color: "grey", height: "100px" }}>
                                    No reviews found.
                                </StyledTableCell>
                            </StyledTableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[10, 20, 30, 60, 100]}
                    component="div"
                    count={totalCount !== undefined ? totalCount : ((data || []).length || 0)}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelDisplayedRows={({ page, count }) => `Page ${page + 1} of ${count !== -1 ? Math.ceil(count / rowsPerPage) : 1}`}
                />
            </TableContainer>
            
            <UploadCsvModal 
                open={isUploadModalOpen} 
                onClose={() => setIsUploadModalOpen(false)} 
                onUploadSuccess={onRefresh}
            />

            <Dialog open={isVerifyModalOpen} onClose={() => !verifying && setIsVerifyModalOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '8px', overflow: 'hidden' } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid #e0e0e0' }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#212529' }}>Bulk Mark as Verified</Typography>
                    <IconButton size="small" onClick={() => !verifying && setIsVerifyModalOpen(false)} sx={{ color: '#6c757d', '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' } }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                <Box sx={{ padding: '24px' }}>
                    <Box sx={{ display: 'flex', gap: '12px', backgroundColor: '#fff8eb', border: '1px solid #ffe0b2', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
                        <ReportProblemOutlinedIcon sx={{ color: '#f57c00', fontSize: '22px', flexShrink: 0, marginTop: '2px' }} />
                        <Typography sx={{ fontSize: '14px', color: '#495057', lineHeight: 1.5 }}>
                            Please confirm that you have manually verified the Quiz, Concept, and Coding results, and have added and saved your feedback and justification for the <strong>{selectedRows.length}</strong> selected learner(s).
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '14px', color: '#495057', lineHeight: 1.6 }}>
                        Once you mark these <strong>{selectedRows.length}</strong> reviews as Verified, they will be locked, and you will no longer be able to make any changes to these learners' reviews.
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '16px 24px', backgroundColor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
                    <Button
                        onClick={() => setIsVerifyModalOpen(false)}
                        disabled={verifying}
                        sx={{ textTransform: 'none', fontWeight: 600, color: '#495057', padding: '6px 16px', borderRadius: '6px', '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' } }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleMarkAsVerified}
                        disabled={verifying}
                        sx={{ textTransform: 'none', fontWeight: 600, backgroundColor: '#0056b3', color: '#fff', padding: '6px 20px', borderRadius: '6px', '&:hover': { backgroundColor: '#004085' } }}
                    >
                        {verifying ? "Confirming..." : "Confirm"}
                    </Button>
                </Box>
            </Dialog>

            <Dialog open={isPublishModalOpen} onClose={() => !publishing && setIsPublishModalOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '8px', overflow: 'hidden' } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid #e0e0e0' }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#212529' }}>Publish Reviews</Typography>
                    <IconButton size="small" onClick={() => !publishing && setIsPublishModalOpen(false)} sx={{ color: '#6c757d', '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' } }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                <Box sx={{ padding: '24px' }}>
                    <Box sx={{ display: 'flex', gap: '12px', backgroundColor: '#e3f2fd', border: '1px solid #bbdefb', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
                        <PublishIcon sx={{ color: '#1976d2', fontSize: '22px', flexShrink: 0, marginTop: '2px' }} />
                        <Typography sx={{ fontSize: '14px', color: '#1e3a8a', lineHeight: 1.5 }}>
                            Please confirm that you want to publish the review results for the <strong>{selectedRows.length}</strong> selected learner(s).
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '14px', color: '#495057', lineHeight: 1.6 }}>
                        Once published, these <strong>{selectedRows.length}</strong> review score(s) will be officially published and available to learners.
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '16px 24px', backgroundColor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
                    <Button
                        onClick={() => setIsPublishModalOpen(false)}
                        disabled={publishing}
                        sx={{ textTransform: 'none', fontWeight: 600, color: '#495057', padding: '6px 16px', borderRadius: '6px', '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' } }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handlePublishReviews}
                        disabled={publishing}
                        sx={{ textTransform: 'none', fontWeight: 600, backgroundColor: '#0056b3', color: '#fff', padding: '6px 20px', borderRadius: '6px', '&:hover': { backgroundColor: '#004085' } }}
                    >
                        {publishing ? "Publishing..." : "Confirm Publish"}
                    </Button>
                </Box>
            </Dialog>
        </Box>
    );
};

export default MentorReviewTable;
